import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    IconButton,
    Avatar,
    AvatarGroup,
    Tooltip,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    AutoAwesome,
    Share,
    Edit,
    Delete,
} from '@mui/icons-material';

export interface PhotoInfoContentPhoto {
    id: string;
    url: string;
    aiCaption?: string;
    aiTags?: string[];
    aiLocationGuess?: string | null;
    tripDestination?: string;
    visibility: 'private' | 'bffs_travel_partners' | 'friends_family' | 'public';
    sharedWith?: string[];
    uploadedAt: string;
    showCreatorName?: boolean | null;
}

export interface PhotoInfoContentFriend {
    id: string;
    name: string;
    avatar?: string;
}

export interface PhotoInfoContentProps<TPhoto extends PhotoInfoContentPhoto = PhotoInfoContentPhoto> {
    photo: TPhoto;
    tripTitle?: string;
    suppressTripTitle?: boolean;
    friends: PhotoInfoContentFriend[];
    photos: TPhoto[];
    currentIndex: number;
    onShare?: (photo: TPhoto) => void;
    onEdit?: (photo: TPhoto) => void;
    onDelete?: (photoId: string) => void;
    onSelectPhoto: (index: number) => void;
    showPaginationDots: boolean;
    editLabel?: string;
    renderBadges?: () => React.ReactNode;
    onToggleCreatorName?: (photoId: string, next: boolean, applyToAllInTrip: boolean) => void;
}

function PhotoInfoContent<TPhoto extends PhotoInfoContentPhoto>({
    photo,
    tripTitle,
    suppressTripTitle,
    friends,
    photos,
    currentIndex,
    onShare,
    onEdit,
    onDelete,
    onSelectPhoto,
    showPaginationDots,
    editLabel,
    renderBadges,
    onToggleCreatorName,
}: PhotoInfoContentProps<TPhoto>) {
    return (
        <>
            {tripTitle && !suppressTripTitle && (
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5, fontSize: { xs: '0.95rem', sm: '1.25rem' }, lineHeight: { xs: 1.2, sm: 1.4 }, '@media (orientation: landscape) and (max-height: 500px)': { mb: 0.25, fontSize: '0.9rem' } }}>
                    {tripTitle}
                </Typography>
            )}
            {photo.aiCaption && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontStyle: 'italic', mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: { xs: 1.3, sm: 1.43 }, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', '@media (orientation: landscape) and (max-height: 500px)': { mb: 0.5, WebkitLineClamp: 3 } }}>
                    {photo.aiCaption}
                </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: { xs: 0.75, sm: 2 }, fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', '@media (orientation: landscape) and (max-height: 500px)': { mb: 0.5 } }}>
                {(photo.aiLocationGuess || photo.tripDestination) && (
                    <>
                        <span>{'\u{1F4CD}'} {photo.aiLocationGuess || photo.tripDestination}</span>
                        <span>{'\u2022'}</span>
                    </>
                )}
                <span>{new Date(photo.uploadedAt).toLocaleDateString()}</span>
                {renderBadges && renderBadges()}
            </Typography>

            {photo.aiTags && photo.aiTags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: { xs: 'nowrap', sm: 'wrap' }, overflowX: { xs: 'auto', sm: 'visible' }, mb: { xs: 0.75, sm: 2 }, '@media (orientation: landscape) and (max-height: 500px)': { mb: 0.5 } }}>
                    <AutoAwesome sx={{ fontSize: 16, color: '#4A6FA5', mr: 0.5 }} />
                    {photo.aiTags.slice(0, 5).map(tag => (
                        <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.1)',
                                color: 'white',
                                fontSize: '0.7rem',
                            }}
                        />
                    ))}
                </Box>
            )}

            {(['bffs_travel_partners', 'friends_family'].includes(photo.visibility)) && photo.sharedWith && photo.sharedWith.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Shared with:
                    </Typography>
                    <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 10, border: '1px solid #000' } }}>
                        {photo.sharedWith.map(fId => {
                            const friend = friends.find(f => f.id === fId);
                            return friend ? (
                                <Tooltip key={fId} title={friend.name}>
                                    <Avatar src={friend.avatar} sx={{ bgcolor: '#2D6E6A' }}>
                                        {friend.name[0]}
                                    </Avatar>
                                </Tooltip>
                            ) : null;
                        })}
                    </AvatarGroup>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {onShare && (<Button
                    variant="contained"
                    size="small"
                    startIcon={<Share />}
                    onClick={() => onShare(photo)}
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        borderRadius: '20px',
                        textTransform: 'none',
                        backdropFilter: 'blur(10px)',
                        py: { xs: 0.25, sm: 0.5 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    }}
                >
                    Peek
                </Button>)}
                {onEdit && (<Button
                    variant="contained"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => onEdit(photo)}
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        borderRadius: '20px',
                        textTransform: 'none',
                        backdropFilter: 'blur(10px)',
                        py: { xs: 0.25, sm: 0.5 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    }}
                >
                    {editLabel || 'Edit'}
                </Button>)}
                {onDelete && (<IconButton
                    size="small"
                    onClick={() => onDelete(photo.id)}
                    sx={{
                        color: '#FF6B6B',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': { bgcolor: 'rgba(255,107,107,0.2)' },
                    }}
                >
                    <Delete />
                </IconButton>)}
            </Box>

            {onToggleCreatorName && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={photo.showCreatorName !== false}
                                onChange={(e) => onToggleCreatorName(photo.id, e.target.checked, false)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>Show my name on this photo</Typography>}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
                        <Button
                            size="small"
                            onClick={() => onToggleCreatorName(photo.id, photo.showCreatorName !== false, true)}
                            sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontSize: '0.7rem', minWidth: 0, p: 0.25 }}
                        >
                            Apply to all photos in this trip
                        </Button>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.5)', mt: 0.25, fontSize: '0.65rem' }}>
                        Only takes effect when "Show my name on public photos" is on in Settings.
                    </Typography>
                </Box>
            )}

            {showPaginationDots && photos.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: { xs: 1, sm: 2 } }}>
                    {photos.slice(0, 10).map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => onSelectPhoto(index)}
                            sx={{
                                width: currentIndex === index ? 16 : 8,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: currentIndex === index ? '#2D6E6A' : 'rgba(255,255,255,0.3)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: currentIndex === index ? '#2D6E6A' : 'rgba(255,255,255,0.5)',
                                },
                            }}
                        />
                    ))}
                    {photos.length > 10 && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', ml: 1 }}>
                            +{photos.length - 10}
                        </Typography>
                    )}
                </Box>
            )}
        </>
    );
}

export default PhotoInfoContent;